export const GROUP_RESERVATION_STATUS_OPTIONS = [
  { value: "", label: "전체" },
  { value: "submitted", label: "접수" },
  { value: "reviewing", label: "검토중" },
  { value: "reservation_created", label: "예약생성" },
  { value: "awaiting_bank_transfer", label: "입금대기" },
  { value: "bank_transfer_confirmed", label: "입금확인" },
  { value: "approved", label: "승인" },
  { value: "reverted", label: "되돌림" },
  { value: "rejected", label: "반려" },
  { value: "canceled", label: "취소" },
];

export const GROUP_RESERVATION_GUIDE_SECTIONS = [
  {
    title: "사전 설정",
    items: [
      "매장설정 > 홈 및 매장 설정에서 단체 예약 신청 노출을 켭니다.",
      "단체예약 계좌 정보에 은행명, 계좌번호, 예금주, 입금 안내 문구를 저장합니다.",
      "단체예약 > 옵션 상품에서 고객 신청 화면에 노출할 옵션과 주중/주말 가격을 등록합니다.",
    ],
  },
  {
    title: "신청 처리",
    items: [
      "신청 관리에서 접수 건을 열고 단체명, 희망일, 인원, 연락처, 선택 옵션을 확인합니다.",
      "필요한 경우 예약 ID를 입력해 실제 예약 건을 연결합니다. 예약 연결이나 입금 확인 없이도 승인할 수 있습니다.",
      "입금 안내 시작을 누르면 현재 매장 계좌 정보가 신청 건에 저장되고 상태가 입금대기로 바뀝니다.",
      "입금 확인은 입금자명과 입금 확인 금액을 기록해야 할 때만 사용합니다.",
      "승인한 신청은 승인 취소로 입금확인, 예약생성, 접수 중 가능한 진행 상태로 되돌릴 수 있습니다.",
    ],
  },
  {
    title: "대리점 운영",
    items: [
      "마케터 권한 계정을 만든 뒤 단체예약 > 대리점 링크에서 대리점 링크를 발급합니다.",
      "대리점 링크로 접수된 신청은 접수 경로와 대리점 수수료가 신청 건에 함께 표시됩니다.",
      "마케터는 마케터 정보 화면에서 본인 정보와 정산 계좌를 입력하고 본인 링크 접수 건을 확인합니다.",
    ],
  },
  {
    title: "상태 및 주의사항",
    items: [
      "단체예약은 신청 접수와 계좌이체 확인 흐름이며, 공개 신청 단계에서는 예약, 주문, PG 결제 건을 만들지 않습니다.",
      "반려와 취소는 연결 예약을 자동 취소하지 않습니다.",
      "연결 예약 되돌림은 승인 전 상태에서만 가능하며, 예약 상태가 입장 이후인 건은 수동 처리해야 합니다.",
      "매장이 운영 일시정지 상태이면 일반 신청과 대리점 링크 신청이 모두 차단됩니다.",
    ],
  },
];

const TERMINAL_STATUSES = new Set(["approved", "reverted", "rejected", "canceled"]);
const APPROVED_STATUS = "approved";
const PENDING_STATUSES = new Set(["submitted", "reviewing", "reservation_created", "awaiting_bank_transfer", "bank_transfer_confirmed"]);

const padDatePart = (value) => String(value).padStart(2, "0");

function toDateKey(date) {
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
}

function parseDateKey(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getGroupReservationStatusLabel(status) {
  return GROUP_RESERVATION_STATUS_OPTIONS.find((option) => option.value === status)?.label || status;
}

export function getGroupReservationSourceLabel(request) {
  if (request?.source_type === "agency") {
    return request.agency_label || "대리점";
  }
  return "일반";
}

export function getGroupReservationTypeLabel(request) {
  if (request?.group_type === "academy") return "학원";
  return "일반";
}

export function getLinkedGroupReservationSourceLabel(groupReservation) {
  if (groupReservation?.source_type === "agency") {
    return groupReservation.agency_label || "대리점";
  }
  return "일반";
}

export function formatLinkedGroupReservationSummary(groupReservation) {
  if (!groupReservation) return "";
  const headcount = Number(groupReservation.headcount || 0);
  return `${groupReservation.group_name || "-"}${headcount > 0 ? ` · ${headcount.toLocaleString()}명` : ""}`;
}

export function formatLinkedGroupReservationPayment(groupReservation) {
  if (!groupReservation) return "-";
  const amount = Number(groupReservation.bank_transfer_amount || 0);
  return amount > 0 ? `${amount.toLocaleString()}원` : "-";
}

export function canStartBankTransfer(request) {
  return ["submitted", "reviewing", "reservation_created"].includes(request?.status);
}

export function canConfirmBankTransfer(request) {
  return request?.status === "awaiting_bank_transfer";
}

export function canApproveGroupReservationRequest(request) {
  return Boolean(request?.status) && PENDING_STATUSES.has(request.status);
}

export function canCancelGroupReservationApproval(request) {
  return request?.status === APPROVED_STATUS;
}

export function canRevertGroupReservationRequest(request) {
  return (
    ["reservation_created", "awaiting_bank_transfer"].includes(request?.status) &&
    Number(request?.linked_reservation_count || 0) > 0
  );
}

export function canRejectGroupReservationRequest(request) {
  return Boolean(request?.status) && !TERMINAL_STATUSES.has(request.status);
}

export function formatGroupReservationDateRange(request) {
  if (!request?.visit_start_date) return "-";
  if (!request.visit_end_date || request.visit_start_date === request.visit_end_date) {
    return request.visit_start_date;
  }
  return `${request.visit_start_date} ~ ${request.visit_end_date}`;
}

export function formatGroupReservationCalendarMonthTitle(monthDate) {
  const date = monthDate instanceof Date ? monthDate : new Date();
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
}

export function buildGroupReservationCalendarWeeks(monthDate, requests = []) {
  const target = monthDate instanceof Date && !Number.isNaN(monthDate.getTime()) ? monthDate : new Date();
  const firstOfMonth = new Date(target.getFullYear(), target.getMonth(), 1);
  const lastOfMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0);
  const firstCalendarDate = new Date(firstOfMonth);
  firstCalendarDate.setDate(firstCalendarDate.getDate() - firstCalendarDate.getDay());
  const lastCalendarDate = new Date(lastOfMonth);
  lastCalendarDate.setDate(lastCalendarDate.getDate() + (6 - lastCalendarDate.getDay()));

  const requestsByDate = new Map();
  requests.forEach((request) => {
    const startDate = parseDateKey(request.visit_start_date);
    const endDate = parseDateKey(request.visit_end_date || request.visit_start_date);
    if (!startDate || !endDate || endDate < startDate) return;
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateKey = toDateKey(currentDate);
      const dateRequests = requestsByDate.get(dateKey) || [];
      dateRequests.push(request);
      requestsByDate.set(dateKey, dateRequests);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  });

  const weeks = [];
  let currentDate = new Date(firstCalendarDate);
  while (currentDate <= lastCalendarDate) {
    const week = [];
    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const dateKey = toDateKey(currentDate);
      week.push({
        date: new Date(currentDate),
        dateKey,
        dayOfMonth: currentDate.getDate(),
        isCurrentMonth: currentDate.getMonth() === target.getMonth(),
        requests: requestsByDate.get(dateKey) || [],
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

export function getGroupReservationCalendarDaySummary(requests = []) {
  return requests.reduce(
    (summary, request) => ({
      requestCount: summary.requestCount + 1,
      headcount: summary.headcount + Number(request.headcount || 0),
      agencyCount: summary.agencyCount + (request.source_type === "agency" ? 1 : 0),
      pendingCount: summary.pendingCount + (PENDING_STATUSES.has(request.status) ? 1 : 0),
      approvedCount: summary.approvedCount + (request.status === APPROVED_STATUS ? 1 : 0),
    }),
    {
      requestCount: 0,
      headcount: 0,
      agencyCount: 0,
      pendingCount: 0,
      approvedCount: 0,
    }
  );
}
